import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';

import { DocType } from '@/models/docs';
import { Undo2 } from 'lucide-react';

import { cn } from '@/lib/utils';

import { useReportUnsavedChanges } from '@/hooks/useUnsavedChangesGuard';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import type { SearchableComboboxItem } from '@/components/ui/searchable-combobox';

import { CopyAsTextButton } from './CopyAsTextButton';
import { EditorFooter, loadErrorMessage } from './EditorFooter';
import { FileNavBar } from './FileNavBar';
import { FileRemarkBox } from './FileRemarkBox';
import {
    DOCUMENTS_SECTION_ID,
    FILE_SECTION_ID,
    LayerChip,
} from './GroundTruthLayerSteps';
import { Hint } from './Hint';
import { LoadError } from './LoadError';
import { PanelSkeleton } from './LoadingSkeletons';
import type { EditorStatus } from './PageSplitEditor';
import { splitTypeLabel } from './PageSplitRow';
import { SheetSplitRow, sheetOrigin } from './SheetSplitRow';
import type { SheetTabState } from './SheetTabs';
import {
    useGtFileDocs,
    useGtFileStatus,
    useGtSheetSplits,
    useSaveGtSheetSplits,
} from './api';
import { fileDisplayName, pluralize } from './fileLabels';
import type { FieldsProgress } from './layerSteps';
import { splitColor } from './splitBands';
import { useSplitShortcuts } from './splitShortcuts';
import { splitsAsText } from './splitsAsText';
import { GtFileDoc, GtSheetSplit, GtSheetSplitInput } from './types';
import { useConfirmNewDocType } from './useConfirmNewDocType';
import {
    flagCustomDocType,
    isUnnamedDocType,
    labelWithAbbreviation,
    useDocTypeOptions,
} from './useDocTypeOptions';
import { useFolderOptions } from './useFolderOptions';
import { answerSetVersion, useGridEditor } from './useGridEditor';

export interface DraftSheet {
    key: string;
    /** 1-based sheet number as shown to the annotator; the API stores it 0-based. */
    sheetIndex: string;
    sheetTitle: string;
    include: boolean;
    docType: string;
    folder: string;
    excludeReason: string;
    notes: string;
    remark: string;
    /** False for a workbook sheet nobody has answered yet; it is left out of the save. */
    answered: boolean;
    /** A person ticked this sheet's answer as Verified. */
    checked: boolean;
    checkedBy: string | null;
    editedBy: string | null;
    /** Who made the answer: 'claude', 'pipeline', or null once a person has changed it. */
    draftedBy: string | null;
    /** Claude flagged this answer as one it was unsure about. */
    unsure: boolean;
}

let nextKey = 0;
const makeKey = () => `sheet-${nextKey++}`;

function emptyDraft(position: number): DraftSheet {
    return {
        key: makeKey(),
        sheetIndex: String(position + 1),
        sheetTitle: '',
        include: true,
        docType: '',
        folder: '',
        excludeReason: '',
        notes: '',
        remark: '',
        answered: true,
        checked: false,
        checkedBy: null,
        editedBy: null,
        draftedBy: null,
        unsure: false,
    };
}

export function seedDrafts(sheets: GtSheetSplit[]): DraftSheet[] {
    return sheets.map((s) => ({
        key: makeKey(),
        sheetIndex: String(s.sheetIndex + 1),
        sheetTitle: s.sheetTitle,
        include: s.include,
        docType: s.docType ?? '',
        folder: s.folder ?? '',
        excludeReason: s.excludeReason ?? '',
        notes: s.notes ?? '',
        remark: s.remark ?? '',
        answered: true,
        checked: s.checked,
        checkedBy: s.checkedBy,
        editedBy: s.editedBy,
        draftedBy: s.draftedBy,
        unsure: s.unsure,
    }));
}

/** One suggested row per workbook sheet that already belongs to a document. */
export function suggestDrafts(
    docs: GtFileDoc[] | undefined,
    sheetNames: string[],
): DraftSheet[] {
    const rows: { position: number; draft: DraftSheet }[] = [];
    const seen = new Set<number>();

    for (const doc of docs ?? []) {
        const position = doc.sheetName ? sheetNames.indexOf(doc.sheetName) : -1;
        if (position < 0 || seen.has(position)) continue;

        seen.add(position);
        rows.push({
            position,
            draft: {
                ...emptyDraft(position),
                sheetTitle: sheetNames[position],
                docType: doc.type === DocType.UNKNOWN_TYPE ? '' : doc.type,
                draftedBy: 'pipeline',
            },
        });
    }

    return rows.sort((a, b) => a.position - b.position).map((r) => r.draft);
}

interface SheetSeedSource {
    saved: GtSheetSplit[];
    suggestions: DraftSheet[];
    sheetNames: string[];
}

/** A row for every workbook sheet: the saved or suggested answers, plus an unanswered row for each sheet without one. */
function seedFromSource({
    saved,
    suggestions,
    sheetNames,
}: SheetSeedSource): DraftSheet[] {
    const rows = saved.length === 0 ? suggestions : seedDrafts(saved);
    const answered = new Set(rows.map((r) => Number(r.sheetIndex) - 1));
    const unanswered = sheetNames.flatMap((name, position) =>
        answered.has(position)
            ? []
            : [
                  {
                      ...emptyDraft(position),
                      sheetTitle: name,
                      answered: false,
                  },
              ],
    );

    return [...rows, ...unanswered].sort(
        (a, b) => Number(a.sheetIndex) - Number(b.sheetIndex),
    );
}

const NO_SHEET_NAMES: string[] = [];
const MAX_UNDO_STEPS = 50;

interface HistoryEntry {
    label: string;
    drafts: DraftSheet[];
}

/** Appends the abbreviation to known types so the combobox shows "Name (ABBR)"; custom types are flagged as not standard. */
function withAbbreviation(
    item: SearchableComboboxItem,
): SearchableComboboxItem {
    if (isUnnamedDocType(item)) return flagCustomDocType(item);
    return {
        ...item,
        label: labelWithAbbreviation(item.label),
    };
}

interface SheetSplitEditorProps {
    ingestedFileId: number;
    /** Workbook sheet names in order; every one gets a row, and sheets that already have a document are suggested. */
    sheetNames?: string[];
    /** The sheet shown in the preview (0-based position); the editor keeps its own when the caller does not. */
    selectedSheet?: number;
    onSelectSheet?: (index: number) => void;
    /** Receives each sheet's answer by 0-based position, so the preview's tabs can match the rows. */
    onSheetStates?: (states: ReadonlyMap<number, SheetTabState>) => void;
    /** Receives the counts the page header shows (null until loaded and on unmount). */
    onStatusChange?: (status: EditorStatus | null) => void;
    /** The next file of the list the person came from, offered once nothing is unsaved. */
    nextFile?: () => void;
    /** Opens the previous file of that list (P); set only when there is one. */
    previousFile?: () => void;
    /** Name of the list the person came from; the Previous and Next buttons show only when set. */
    listName?: string | null;
}

/**
 * Layer 2 editor: per-worksheet document-or-leave-out answers for an ingested Excel file.
 * Keyed by `ingestedFileId` (resolved by the caller); the workbook preview beside it is composed by the caller.
 */
export function SheetSplitEditor(props: SheetSplitEditorProps) {
    // Keyed so drafts, a save conflict and the saved version reset when the route reuses this for another file.
    return <SheetSplitEditorForFile key={props.ingestedFileId} {...props} />;
}

function SheetSplitEditorForFile({
    ingestedFileId,
    sheetNames = NO_SHEET_NAMES,
    selectedSheet,
    onSelectSheet,
    onSheetStates,
    onStatusChange,
    nextFile,
    previousFile,
    listName = null,
}: SheetSplitEditorProps) {
    const {
        data: saved,
        isLoading,
        isFetching,
        isError: savedError,
        isRefetching: savedRefetching,
        refetch: refetchSaved,
    } = useGtSheetSplits(ingestedFileId);
    const { data: docs } = useGtFileDocs(ingestedFileId);
    const existing = useMemo(
        () =>
            saved
                ? {
                      saved,
                      suggestions: suggestDrafts(docs, sheetNames),
                      sheetNames,
                  }
                : undefined,
        [saved, docs, sheetNames],
    );
    const { data: docTypes } = useDocTypeOptions();
    const typeItems = useMemo(
        () => (docTypes ?? []).map(withAbbreviation),
        [docTypes],
    );
    const { requestCreate: requestNewDocType, dialog: newDocTypeDialog } =
        useConfirmNewDocType(docTypes ?? [], 'SheetSplitEditor');
    const {
        data: folderOptions,
        isLoading: foldersLoading,
        isError: foldersError,
        isRefetching: foldersRefetching,
        refetch: refetchFolders,
        emptyMessage: folderEmptyMessage,
    } = useFolderOptions();
    const foldersFailed = foldersError && folderOptions.length === 0;
    const save = useSaveGtSheetSplits(ingestedFileId);

    const {
        drafts,
        setDrafts,
        updateDraft,
        errors,
        isDirty,
        hasEdits,
        clearCount,
        onSave,
        conflict,
        reloadSaved: reloadSavedSheets,
    } = useGridEditor<
        DraftSheet,
        GtSheetSplitInput[],
        SheetSeedSource | undefined
    >({
        savedCount: saved?.length ?? 0,
        savedVersion: saved && answerSetVersion(saved),
        reload: refetchSaved,
        existing,
        seed: seedFromSource,
        // Suggested rows are not saved yet, so they count as unsaved from the start.
        seedIsUnsaved: ({ saved: rows, suggestions }) =>
            rows.length === 0 && suggestions.length > 0,
        validate: validateDrafts,
        save,
        successMessage: 'Sheet documents saved',
        entityType: 'sheets',
    });

    // One entry per undoable change, holding the rows from before it (the Verified ticks ride in the rows).
    const [history, setHistory] = useState<HistoryEntry[]>([]);
    // The field a run of typing is in; it joins into one undo step until focus leaves the field.
    const typingRunRef = useRef<string | null>(null);
    const draftsRef = useRef(drafts);
    draftsRef.current = drafts;
    const recordUndo = (label: string, typingRun?: string) => {
        if (typingRun !== undefined && typingRun === typingRunRef.current)
            return;
        typingRunRef.current = typingRun ?? null;
        const entry = { label, drafts: draftsRef.current };
        setHistory((entries) => [
            ...entries.slice(-(MAX_UNDO_STEPS - 1)),
            entry,
        ]);
    };
    const undo = () => {
        const last = history.at(-1);
        if (!last) return false;
        typingRunRef.current = null;
        setHistory(history.slice(0, -1));
        setDrafts(last.drafts);
        return true;
    };
    const undoLabel = history.at(-1)?.label;
    const reloadSaved = () => {
        typingRunRef.current = null;
        setHistory([]);
        reloadSavedSheets();
    };

    const [internalSelected, setInternalSelected] = useState(0);
    const selected = selectedSheet ?? internalSelected;
    const select = (index: number) => {
        setInternalSelected(index);
        onSelectSheet?.(index);
    };
    const positionOf = (row: DraftSheet) => Number(row.sheetIndex) - 1;
    const [expandedKey, setExpandedKey] = useState<string | null>(null);
    const toggleRow = (row: DraftSheet) => {
        select(positionOf(row));
        setExpandedKey((key) => (key === row.key ? null : row.key));
    };

    const answered = drafts.filter((d) => d.answered);
    const documentCount = answered.filter((d) => d.include).length;
    const leftOutCount = answered.length - documentCount;
    const unansweredCount = drafts.length - answered.length;
    const verifiedCount = answered.filter((d) => d.checked).length;
    const unsureCount = answered.filter(
        (d) => d.unsure && !d.checked && sheetOrigin(d.draftedBy) === 'Claude',
    ).length;
    const verifyAllState: boolean | 'indeterminate' =
        verifiedCount === 0
            ? false
            : verifiedCount === answered.length
              ? true
              : 'indeterminate';
    const unsavedCount = useMemo(
        () => countUnsaved(validateDrafts(drafts).payload, saved),
        [drafts, saved],
    );
    const dirty = isDirty || unsavedCount > 0;
    const { data: fileStatus } = useGtFileStatus(ingestedFileId);
    const sheetName = (d: DraftSheet) =>
        d.sheetTitle ? `Sheet "${d.sheetTitle}"` : `Sheet ${d.sheetIndex}`;
    const copyAsText = () =>
        splitsAsText({
            fileName: fileDisplayName(fileStatus),
            sizeText: pluralize(drafts.length, 'sheet'),
            url: `${window.location.origin}/ground-truth/${ingestedFileId}`,
            rows: answered.map((d) => ({
                name: sheetName(d),
                type: d.docType
                    ? splitTypeLabel(docTypes ?? [], d.docType)
                    : null,
                verified: d.checked,
                note: d.remark,
                leftOutReason: d.include ? undefined : d.excludeReason,
            })),
            leftOutText: '',
            gapsText: drafts
                .filter((d) => !d.answered)
                .map(sheetName)
                .join(', '),
            gapsLabel: 'No answer yet',
            fileNote: fileStatus?.fileRemark ?? '',
            verifiedCount,
            unsavedCount,
        });

    // Changing what a sheet is clears its Verified tick and makes the person its author.
    const answer = (
        row: DraftSheet,
        patch: Partial<Pick<DraftSheet, 'include' | 'docType'>>,
    ) => {
        const include = patch.include ?? row.include;
        const docType = patch.docType ?? row.docType;
        if (row.answered && include === row.include && docType === row.docType)
            return;
        recordUndo(
            patch.include === undefined
                ? `type of ${row.sheetTitle}`
                : `${include ? 'include' : 'leave out'} ${row.sheetTitle}`,
        );
        updateDraft(row.key, {
            include,
            docType,
            answered: true,
            checked: false,
            checkedBy: null,
            editedBy: null,
            draftedBy: null,
            unsure: false,
        });
    };
    const editDetail = (
        row: DraftSheet,
        patch: Partial<DraftSheet>,
        field: string,
    ) => {
        recordUndo(`${field} of ${row.sheetTitle}`, `${row.key}:${field}`);
        updateDraft(row.key, patch);
    };
    const setChecked = (row: DraftSheet, checked: boolean) =>
        updateDraft(row.key, { checked, checkedBy: null });
    const verifyAll = (value: boolean) => {
        recordUndo(value ? 'verify all' : 'unverify all');
        setDrafts((rows) =>
            rows.map((r) =>
                r.answered ? { ...r, checked: value, checkedBy: null } : r,
            ),
        );
    };
    const stepSheet = (direction: 1 | -1) => {
        const rows = draftsRef.current;
        const at = rows.findIndex((r) => positionOf(r) === selected);
        const target =
            rows[Math.min(Math.max(at + direction, 0), rows.length - 1)];
        if (target) select(positionOf(target));
    };
    const verifyOpenSheet = () => {
        const row = draftsRef.current.find((r) => r.key === expandedKey);
        if (row?.answered) setChecked(row, !row.checked);
    };

    const primaryRef = useRef<HTMLButtonElement>(null);
    useSplitShortcuts({
        undo,
        previousSplit: () => stepSheet(-1),
        nextSplit: () => stepSheet(1),
        save: () => {
            if (dirty) primaryRef.current?.click();
        },
        nextFile,
        previousFile,
        verify: verifyOpenSheet,
        // Page keys have no meaning on a workbook.
        previousPage: () => undefined,
        nextPage: () => undefined,
        splitHere: () => undefined,
        useAsStart: () => undefined,
        useAsEnd: () => undefined,
    });

    useReportUnsavedChanges(hasEdits);

    const tabStates = useMemo(
        () =>
            new Map<number, SheetTabState>(
                drafts.map((d) => [
                    Number(d.sheetIndex) - 1,
                    d.answered ? { include: d.include } : null,
                ]),
            ),
        [drafts],
    );
    useEffect(() => onSheetStates?.(tabStates), [tabStates, onSheetStates]);

    const fieldsSig = answered
        .flatMap((d) => {
            const doc = d.include
                ? docs?.find((x) => x.sheetName === d.sheetTitle)
                : undefined;
            return doc && doc.fieldsTotal > 0
                ? [`${doc.fieldsFilled}/${doc.fieldsTotal}`]
                : [];
        })
        .join(',');
    const fieldsProgress = useMemo<FieldsProgress[]>(
        () =>
            fieldsSig === ''
                ? []
                : fieldsSig.split(',').map((part) => {
                      const [filled, total] = part.split('/').map(Number);
                      return { filled, total };
                  }),
        [fieldsSig],
    );
    const documentsVerified = answered.filter(
        (d) => d.include && d.checked,
    ).length;
    const ready = !isLoading && saved !== undefined;
    useEffect(() => {
        onStatusChange?.(
            ready
                ? {
                      splitCount: answered.length,
                      verifiedCount,
                      documentCount,
                      documentsVerified,
                      fields: fieldsProgress,
                      unsavedCount,
                      coverage: {
                          unit: 'sheets',
                          total: drafts.length,
                          missing: unansweredCount,
                          empty: answered.length === 0,
                      },
                  }
                : null,
        );
    }, [
        ready,
        answered.length,
        drafts.length,
        unansweredCount,
        verifiedCount,
        documentCount,
        documentsVerified,
        fieldsProgress,
        unsavedCount,
        onStatusChange,
    ]);
    useEffect(() => () => onStatusChange?.(null), [onStatusChange]);

    const saveLabel = dirty
        ? `Save ${unsavedCount} change${unsavedCount === 1 ? '' : 's'}  (⌘S)`
        : 'Save sheet documents  (⌘S)';
    const verifyLocked = save.isPending || isFetching || answered.length === 0;

    return (
        <div
            className="flex h-full min-h-0 flex-col"
            data-testid="SheetSplitEditor"
        >
            {newDocTypeDialog}

            <div className="flex shrink-0 flex-col gap-2 border-b border-[#E2F0F0] px-5 pb-3 pt-4">
                <h2
                    id={FILE_SECTION_ID}
                    className="flex scroll-mt-4 items-center gap-2 text-[15px] font-semibold"
                >
                    <LayerChip step="file" />
                    File
                </h2>
                {ready && drafts.length > 0 && (
                    <CoverageBox
                        unansweredCount={unansweredCount}
                        documentCount={documentCount}
                        leftOutCount={leftOutCount}
                        unsureCount={unsureCount}
                    />
                )}
                <FileRemarkBox ingestedFileId={ingestedFileId} />
            </div>

            <div className="flex-1 space-y-2 overflow-auto px-5 py-3">
                {foldersFailed && (
                    <LoadError
                        what="the binder folders"
                        onRetry={() => void refetchFolders()}
                        isRetrying={foldersRefetching}
                        className="mb-3"
                        data-testid="SheetSplitEditor__foldersError"
                    />
                )}
                {saved === undefined && savedError && (
                    <LoadError
                        what="the saved sheet documents"
                        onRetry={() => void refetchSaved()}
                        isRetrying={savedRefetching}
                        data-testid="SheetSplitEditor__error"
                    />
                )}
                {isLoading ? (
                    <PanelSkeleton
                        rows={3}
                        data-testid="SheetSplitEditor__loading"
                    />
                ) : (
                    <>
                        <div className="flex items-baseline justify-between gap-2">
                            <h2
                                id={DOCUMENTS_SECTION_ID}
                                className="flex min-w-0 scroll-mt-4 flex-wrap items-center gap-2 text-[15px] font-semibold"
                                data-testid="SheetSplitEditor__heading"
                            >
                                <LayerChip step="documents" />
                                Documents in this workbook: sheets (
                                {drafts.length}){' '}
                                <span className="text-[13px] font-normal text-slate-600">
                                    · {documentCount}{' '}
                                    {documentCount === 1
                                        ? 'document'
                                        : 'documents'}
                                    , {leftOutCount} left out
                                </span>
                            </h2>
                            <div className="flex shrink-0 items-center gap-2">
                                <CopyAsTextButton
                                    getText={copyAsText}
                                    testId="SheetSplitEditor__copyMarkdown"
                                />
                                {undoLabel !== undefined && (
                                    <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        className="h-8 shrink-0 text-xs"
                                        onClick={undo}
                                        title={`Undo ${undoLabel}`}
                                        aria-label={`Undo ${undoLabel}`}
                                        data-testid="SheetSplitEditor__undoButton"
                                    >
                                        <Undo2 className="mr-1 h-3 w-3" />
                                        Undo
                                        <Kbd className="ml-2">⌘Z</Kbd>
                                    </Button>
                                )}
                            </div>
                        </div>
                        <ul className="space-y-2">
                            {drafts.map((row) => {
                                const matchedDoc = docs?.find(
                                    (d) => d.sheetName === row.sheetTitle,
                                );
                                return (
                                    <SheetSplitRow
                                        key={row.key}
                                        row={row}
                                        number={positionOf(row) + 1}
                                        color={splitColor(positionOf(row) + 1)}
                                        isSelected={
                                            positionOf(row) === selected
                                        }
                                        isExpanded={expandedKey === row.key}
                                        onToggle={() => toggleRow(row)}
                                        typeText={splitTypeLabel(
                                            docTypes ?? [],
                                            row.docType,
                                        )}
                                        docTypes={typeItems}
                                        folderOptions={folderOptions}
                                        foldersLoading={foldersLoading}
                                        folderEmptyMessage={folderEmptyMessage}
                                        ingestedFileId={ingestedFileId}
                                        matchedDocId={matchedDoc?.id ?? null}
                                        fieldsFilled={matchedDoc?.fieldsFilled}
                                        fieldsTotal={matchedDoc?.fieldsTotal}
                                        onAnswer={(patch) => answer(row, patch)}
                                        onEdit={(patch, field) =>
                                            editDetail(row, patch, field)
                                        }
                                        onCheckedChange={(value) =>
                                            setChecked(row, value)
                                        }
                                        onCreateDocType={(name) =>
                                            requestNewDocType(name, (docType) =>
                                                answer(row, { docType }),
                                            )
                                        }
                                        onFieldBlur={() => {
                                            typingRunRef.current = null;
                                        }}
                                    />
                                );
                            })}
                        </ul>
                        {drafts.length === 0 && saved !== undefined && (
                            <p
                                className="text-sm text-slate-600"
                                data-testid="SheetSplitEditor__empty"
                            >
                                No sheets found in this workbook yet.
                            </p>
                        )}
                    </>
                )}
            </div>

            <EditorFooter
                leading={
                    <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2 text-sm">
                        <Hint
                            text="Tick every sheet as Verified."
                            focusable={verifyLocked}
                        >
                            <label className="flex items-center gap-2 whitespace-nowrap">
                                <Checkbox
                                    checked={verifyAllState}
                                    onCheckedChange={(value) =>
                                        verifyAll(value === true)
                                    }
                                    disabled={verifyLocked}
                                    aria-label="Verify all sheets"
                                    data-testid="SheetSplitEditor__verifyAll"
                                />
                                Verify all{' '}
                                <span className="text-[13px] text-slate-600">
                                    ({verifiedCount} of {answered.length})
                                </span>
                            </label>
                        </Hint>
                    </div>
                }
                errors={errors}
                onSave={onSave}
                isPending={save.isPending}
                disabled={saved === undefined}
                loadError={
                    saved === undefined && savedError
                        ? loadErrorMessage('sheet documents')
                        : null
                }
                clearCount={clearCount}
                clearNoun="sheet document"
                conflict={conflict}
                onReload={reloadSaved}
                saveLabel={saveLabel}
                primaryRef={primaryRef}
                testId="SheetSplitEditor__saveButton"
            />
            {listName && (
                <FileNavBar
                    listName={listName}
                    onPrevious={previousFile}
                    onNext={nextFile}
                />
            )}
        </div>
    );
}

/** Green when every sheet has an answer; amber, naming how many do not, otherwise. */
function CoverageBox({
    unansweredCount,
    documentCount,
    leftOutCount,
    unsureCount,
}: {
    unansweredCount: number;
    documentCount: number;
    leftOutCount: number;
    unsureCount: number;
}) {
    const complete = unansweredCount === 0;
    return (
        <div
            className={cn(
                'flex flex-col gap-1 rounded-lg border px-3 py-2 text-[13px] leading-[18px]',
                complete
                    ? 'border-green-200 bg-green-50 text-green-800'
                    : 'border-amber-200 bg-amber-50 text-amber-800',
            )}
            data-testid="SheetSplitEditor__coverage"
        >
            <span className="font-semibold">
                {complete
                    ? '✓ Every sheet has an answer'
                    : `${unansweredCount} ${unansweredCount === 1 ? 'sheet has' : 'sheets have'} no answer yet`}
            </span>
            <span>
                {complete
                    ? `${documentCount} become ${documentCount === 1 ? 'a document' : 'documents'}, ${leftOutCount} left out.`
                    : 'Open each one and pick Its own document or Leave out.'}
                {unsureCount > 0 && ` Claude was unsure about ${unsureCount}.`}
            </span>
        </div>
    );
}

function Kbd({
    children,
    className,
}: {
    children: ReactNode;
    className?: string;
}) {
    return (
        <kbd
            className={cn(
                'rounded border border-b-2 border-[#CBE1E1] bg-white px-2 font-mono text-xs',
                className,
            )}
        >
            {children}
        </kbd>
    );
}

/** How many answers the save would change: rows whose answer differs from the saved one, and saved rows no longer answered. */
export function countUnsaved(
    payload: GtSheetSplitInput[],
    saved: GtSheetSplit[] | undefined,
): number {
    if (saved === undefined) return 0;

    const savedByIndex = new Map(saved.map((s) => [s.sheetIndex, s]));
    const changed = payload.filter((p) => {
        const s = savedByIndex.get(p.sheetIndex);
        return (
            !s ||
            p.include !== s.include ||
            p.docType !== s.docType ||
            p.folder !== s.folder ||
            p.excludeReason !== s.excludeReason ||
            p.notes !== s.notes ||
            (p.remark ?? null) !== (s.remark ?? null) ||
            p.checked !== s.checked
        );
    }).length;
    const kept = new Set(payload.map((p) => p.sheetIndex));
    const dropped = saved.filter((s) => !kept.has(s.sheetIndex)).length;

    return changed + dropped;
}

export function validateDrafts(drafts: DraftSheet[]): {
    errors: string[];
    payload: GtSheetSplitInput[];
} {
    const errors: string[] = [];
    const payload: GtSheetSplitInput[] = [];
    const seenIndexes = new Set<number>();

    drafts.forEach((d, i) => {
        // A sheet nobody has answered is not saved, so the save never invents an answer.
        if (!d.answered) return;

        const label = `Sheet ${i + 1}`;
        const index = Number(d.sheetIndex);

        if (d.sheetIndex === '' || !Number.isInteger(index) || index < 1) {
            errors.push(`${label}: index must be a whole number from 1.`);
            return;
        }
        if (seenIndexes.has(index)) {
            errors.push(`${label}: duplicate sheet index ${index}.`);
            return;
        }
        seenIndexes.add(index);

        if (!d.sheetTitle.trim()) {
            errors.push(`${label}: a sheet title is required.`);
            return;
        }
        payload.push({
            sheetIndex: index - 1,
            sheetTitle: d.sheetTitle.trim(),
            include: d.include,
            docType: d.include && d.docType ? d.docType : null,
            folder: d.include && d.folder ? d.folder : null,
            excludeReason:
                !d.include && d.excludeReason.trim()
                    ? d.excludeReason.trim()
                    : null,
            notes: d.notes.trim() ? d.notes.trim() : null,
            remark: d.remark.trim() ? d.remark.trim() : null,
            checked: d.checked,
            // Claude's flag stays on an answer nobody has changed or verified.
            unsure: d.unsure && !d.checked && d.draftedBy !== null,
        });
    });

    return { errors, payload };
}
